import { type ReactNode } from 'react';

export interface LinkifyPattern {
  /** Stateful (`g`) regex used to scan plain text nodes. */
  pattern: RegExp;
  /** Build the `href` for a matched substring. */
  href: (match: string) => string;
  /** Optional className applied to the rendered `<a>`. */
  className?: string;
}

export interface MarkdownProps {
  /**
   * Markdown source. Supports headings (`#`/`##` → h2, `###` → h3),
   * `**bold**`, `` `inline code` ``, `-`/`*` bullet lists, `1.` ordered
   * lists, and paragraphs.
   */
  children: string;
  className?: string;
  dataTestId?: string;
  /**
   * Optional post-render linkify transforms. Each pattern is applied to the
   * plain-text segments left after the inline pass (bold + inline-code are
   * never re-scanned). Matches are wrapped in an `<a href={href(match)}>`.
   * Patterns are evaluated in array order; the first to match a given
   * substring wins. Default off — pass an empty array or omit for legacy
   * read-only rendering.
   */
  linkifyPatterns?: LinkifyPattern[];
}

/** Apply linkify patterns to a plain-text segment. */
function renderLinkified(
  text: string,
  patterns: LinkifyPattern[] | undefined,
  keyPrefix: string
): ReactNode {
  if (!patterns || patterns.length === 0 || text === '') return text;
  const matches: { start: number; end: number; pattern: LinkifyPattern }[] = [];
  for (const lp of patterns) {
    const re = new RegExp(lp.pattern.source, lp.pattern.flags.includes('g')
      ? lp.pattern.flags
      : lp.pattern.flags + 'g');
    let m: RegExpExecArray | null;
    while ((m = re.exec(text)) !== null) {
      if (m[0].length === 0) {
        re.lastIndex += 1;
        continue;
      }
      matches.push({ start: m.index, end: m.index + m[0].length, pattern: lp });
    }
  }
  if (matches.length === 0) return text;
  matches.sort((a, b) => a.start - b.start || b.end - a.end);
  const resolved: typeof matches = [];
  let cursor = 0;
  for (const mm of matches) {
    if (mm.start < cursor) continue;
    resolved.push(mm);
    cursor = mm.end;
  }
  const out: ReactNode[] = [];
  let last = 0;
  let key = 0;
  for (const mm of resolved) {
    if (mm.start > last) out.push(text.slice(last, mm.start));
    const matchText = text.slice(mm.start, mm.end);
    out.push(
      <a
        key={`${keyPrefix}-${key++}`}
        href={mm.pattern.href(matchText)}
        className={['pn-markdown-link', mm.pattern.className]
          .filter(Boolean)
          .join(' ')}
      >
        {matchText}
      </a>
    );
    last = mm.end;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

/** Inline pass — `**bold**` and `` `code` ``. */
function renderInline(
  text: string,
  linkifyPatterns: LinkifyPattern[] | undefined,
  blockKey: number
): ReactNode {
  const tokens: ReactNode[] = [];
  const regex = /(`[^`]+`|\*\*[^*]+\*\*)/g;
  let last = 0;
  let key = 0;
  let m: RegExpExecArray | null;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) {
      tokens.push(
        renderLinkified(
          text.slice(last, m.index),
          linkifyPatterns,
          `b${blockKey}-t${key++}`
        )
      );
    }
    const tok = m[0];
    if (tok.startsWith('`')) {
      tokens.push(
        <code key={key++} className="pn-markdown-code">
          {tok.slice(1, -1)}
        </code>
      );
    } else {
      tokens.push(<strong key={key++}>{tok.slice(2, -2)}</strong>);
    }
    last = regex.lastIndex;
  }
  if (last < text.length) {
    tokens.push(
      renderLinkified(
        text.slice(last),
        linkifyPatterns,
        `b${blockKey}-t${key++}`
      )
    );
  }
  return tokens;
}

/**
 * Minimal read-only markdown renderer. Builds React nodes directly — no
 * `dangerouslySetInnerHTML`, no dependency. Covers the subset the agent-prompt
 * editors need: headings, bold, inline code, bullet/ordered lists, paragraphs.
 * For the role-rules Preview + Member read-only panels. CSS namespace
 * `pn-markdown`.
 */
export function Markdown({
  children,
  className,
  dataTestId,
  linkifyPatterns
}: MarkdownProps) {
  const cls = ['pn-markdown', className].filter(Boolean).join(' ');
  const lines = (children ?? '').split('\n');

  const blocks: ReactNode[] = [];
  let listItems: ReactNode[] = [];
  let listOrdered = false;
  let key = 0;

  const flushList = () => {
    if (listItems.length === 0) return;
    const items = listItems;
    blocks.push(
      listOrdered ? (
        <ol key={key++}>{items}</ol>
      ) : (
        <ul key={key++}>{items}</ul>
      )
    );
    listItems = [];
  };

  for (const raw of lines) {
    const line = raw.replace(/\s+$/, '');
    let match: RegExpMatchArray | null;
    if ((match = line.match(/^### (.*)/))) {
      flushList();
      blocks.push(
        <h3 key={key++}>{renderInline(match[1], linkifyPatterns, key)}</h3>
      );
    } else if ((match = line.match(/^#{1,2} (.*)/))) {
      flushList();
      blocks.push(
        <h2 key={key++}>{renderInline(match[1], linkifyPatterns, key)}</h2>
      );
    } else if ((match = line.match(/^[-*] (.*)/))) {
      if (listOrdered) flushList();
      listOrdered = false;
      listItems.push(
        <li key={`li-${key++}`}>
          {renderInline(match[1], linkifyPatterns, key)}
        </li>
      );
    } else if ((match = line.match(/^\d+\. (.*)/))) {
      if (!listOrdered) flushList();
      listOrdered = true;
      listItems.push(
        <li key={`li-${key++}`}>
          {renderInline(match[1], linkifyPatterns, key)}
        </li>
      );
    } else if (line === '') {
      flushList();
      blocks.push(<p key={key++} className="pn-markdown-blank" aria-hidden="true" />);
    } else {
      flushList();
      blocks.push(
        <p key={key++}>{renderInline(line, linkifyPatterns, key)}</p>
      );
    }
  }
  flushList();

  return (
    <div className={cls} data-testid={dataTestId}>
      {blocks.length > 0 ? (
        blocks
      ) : (
        <p className="pn-markdown-empty">(nothing to render)</p>
      )}
    </div>
  );
}
